import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import apiClient from '../api/client';
import { useLanguage } from '../context/LanguageContext';
import { useNotification } from '../components/Notification';
import HomePageBuilderView from '../components/HomePageBuilderView';
import './Home.css';

function Home() {
  const { language, t } = useLanguage();
  const queryClient = useQueryClient();
  const notification = useNotification();

  const addToCartMutation = useMutation({
    mutationFn: async ({ product_id }) => {
      const sessionId = localStorage.getItem('sessionId') || 'sess_' + Date.now();
      localStorage.setItem('sessionId', sessionId);
      return apiClient.post(
        '/cart/add',
        { product_id, variant_id: null, quantity: 1 },
        { headers: { 'x-session-id': sessionId } }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['cart']);
      notification.success(t('addedToCart'));
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || t('errorAddingToCart'));
    },
  });
  const { data: builderData } = useQuery({
    queryKey: ['home-page-builder-public', language],
    queryFn: async () => {
      const res = await apiClient.get('/home-page-builder/public', {
        params: { lang: language || 'lt', _: Date.now() },
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      });
      return res.data;
    },
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  const { data: featuredProducts } = useQuery({
    queryKey: ['featured-products'],
    queryFn: async () => {
      const res = await apiClient.get('/products?limit=8');
      return res.data;
    },
  });

  const useBuilder = Array.isArray(builderData?.content?.sections) && builderData.content.sections.length > 0;

  if (useBuilder) {
    return (
      <div className="home">
        <HomePageBuilderView />
      </div>
    );
  }

  return (
    <div className="home">
      {/* Hero - Built Different */}
      <section className="hero-oa">
        <div className="hero-oa-container">
          <div className="hero-oa-text">
            <h1>{t('qualityGoods')}</h1>
            <p className="hero-oa-subtitle">{t('bestGoods')}</p>
          </div>
          
          {/* Brand Logos */}
          <div className="hero-brands">
            <div className="brand-logo">The Independent</div>
            <div className="brand-logo">Marie Claire</div>
            <div className="brand-logo">GQ</div>
            <div className="brand-logo">Fashion Network</div>
          </div>

          {/* Shop Now Section */}
          <div className="shop-now-section">
            <h2 className="shop-now-title">{t('shopNow')}</h2>
            <div className="shop-now-products">
              {featuredProducts?.products?.slice(0, 3).map((product) => (
                <div key={product.id} className="shop-now-product-wrap">
                  <Link to={`/products/${product.slug}`} className="shop-now-product">
                    <div className="shop-now-image">
                      {product.primary_image ? (
                        <img src={product.primary_image} alt={product.name} />
                      ) : (
                        <div className="shop-now-fallback"></div>
                      )}
                    </div>
                    <div className="shop-now-info">
                      <h3>{product.name}</h3>
                      <p className="shop-now-price">
                        {product.price ? parseFloat(product.price).toFixed(2) : '0.00'} €
                      </p>
                    </div>
                  </Link>
                  <button
                    type="button"
                    className="shop-now-add-to-cart"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      addToCartMutation.mutate({ product_id: product.id });
                    }}
                    disabled={addToCartMutation.isLoading}
                  >
                    {t('addToCart')}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Feature 1 - UnmatchedStability */}
      <section className="feature-oa">
        <div className="feature-oa-content">
          <h2 className="feature-oa-title">{t('quality')}</h2>
          <p className="feature-oa-text">{t('qualityText')}</p>
        </div>
        <div className="feature-oa-image">
          <div className="feature-oa-fallback"></div>
        </div>
      </section>

      {/* Feature 2 - MovementEfficiency */}
      <section className="feature-oa reverse">
        <div className="feature-oa-content">
          <h2 className="feature-oa-title">{t('fastDelivery')}</h2>
          <p className="feature-oa-text">{t('fastDeliveryText')}</p>
        </div>
        <div className="feature-oa-image">
          <div className="feature-oa-fallback"></div>
        </div>
      </section>

      {/* Feature 3 - ReducedFatigue */}
      <section className="feature-oa">
        <div className="feature-oa-content">
          <h2 className="feature-oa-title">{t('greatService')}</h2>
          <p className="feature-oa-text">{t('greatServiceText')}</p>
        </div>
        <div className="feature-oa-image">
          <div className="feature-oa-fallback"></div>
        </div>
      </section>

      {/* Become the elite */}
      <section className="become-elite">
        <div className="become-elite-container">
          <div className="become-elite-content">
            <h2 className="become-elite-title">{t('discoverMore')}</h2>
            <p className="become-elite-text">{t('discoverMoreText')}</p>
            <Link to="/products" className="become-elite-link">{t('viewFullRange')}</Link>
          </div>
          <div className="become-elite-images">
            <div className="become-elite-image">
              <div className="become-elite-fallback"></div>
            </div>
            <div className="become-elite-image">
              <div className="become-elite-fallback"></div>
            </div>
            <div className="become-elite-image">
              <div className="become-elite-fallback"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="testimonial-oa">
        <div className="testimonial-oa-container">
          <blockquote className="testimonial-oa-quote">"{t('testimonialQuote')}"</blockquote>
          <div className="testimonial-oa-author">
            <p className="testimonial-oa-name">Jonas P.</p>
            <p className="testimonial-oa-role">{t('regularCustomer')}</p>
          </div>
          <div className="testimonial-oa-products">
            {featuredProducts?.products?.slice(0, 2).map((product) => (
              <Link
                key={product.id}
                to={`/products/${product.slug}`}
                className="testimonial-oa-product"
              >
                <div className="testimonial-oa-product-image">
                  {product.primary_image ? (
                    <img src={product.primary_image} alt={product.name} />
                  ) : (
                    <div className="testimonial-oa-fallback"></div>
                  )}
                </div>
                <div className="testimonial-oa-product-info">
                  <h3>{product.name}</h3>
                  <p className="testimonial-oa-price">
                    {product.price ? parseFloat(product.price).toFixed(2) : '0.00'} €
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* A newlevel */}
      <section className="newlevel">
        <div className="newlevel-container">
          <div className="newlevel-content">
            <h2 className="newlevel-title">{t('newLevel')}</h2>
            <p className="newlevel-text">{t('newLevelText')}</p>
            <Link to="/products" className="newlevel-link">{t('viewMore')}</Link>
          </div>
          <div className="newlevel-video">
            <div className="newlevel-video-fallback">
              <div className="newlevel-video-play">▶</div>
            </div>
          </div>
          <div className="newlevel-buttons">
            <Link to="/products" className="newlevel-button">{t('allProducts')}</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
